/**
 * Simplified YouTube Transcript Fetcher
 * Frontend-only implementation with multiple fallback methods
 */

/**
 * Extract video ID from YouTube URL
 * @param {string} url - YouTube URL
 * @returns {string|null} Video ID or null
 */
export function extractVideoId(url) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|m\.youtube\.com\/watch\?v=)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) {
      return match[1]
    }
  }
  return null
}

/**
 * Fetch video information using oEmbed API
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<Object|null>} Video information
 */
export async function getVideoInfo(videoId) {
  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`)
    if (!response.ok) throw new Error('Failed to fetch video info')
    
    const data = await response.json()
    return {
      title: data.title,
      author: data.author_name,
      thumbnailUrl: data.thumbnail_url
    }
  } catch (error) {
    console.warn('Could not fetch video info:', error)
    return null
  }
}

/**
 * Available CORS proxy services
 */
const CORS_PROXIES = [
  'https://corsproxy.io/?',
  'https://api.allorigins.win/raw?url=',
  'https://cors.bridged.cc/',
  'https://cors-proxy.htmldriven.com/?url=',
  'https://thingproxy.freeboard.io/fetch/',
  'https://api.codetabs.com/v1/proxy?quest=',
]

/**
 * Make a CORS-enabled request
 * @param {string} url - Target URL
 * @returns {Promise<string>} Response text
 */
async function makeProxiedRequest(url) {
  for (const proxy of CORS_PROXIES) {
    try {
      let proxyUrl
      let response
      
      // Build the proxy URL based on the service
      if (proxy.includes('allorigins')) {
        proxyUrl = proxy + encodeURIComponent(url)
        response = await fetch(proxyUrl)
        if (response.ok) {
          // allorigins returns raw data with raw endpoint
          const text = await response.text()
          if (text && text.length > 0) return text
        }
      } else if (proxy.includes('corsproxy.io')) {
        proxyUrl = proxy + encodeURIComponent(url)
        response = await fetch(proxyUrl)
        if (response.ok) {
          const text = await response.text()
          if (text && text.length > 0) return text
        }
      } else if (proxy.includes('bridged.cc')) {
        proxyUrl = proxy + url
        response = await fetch(proxyUrl, {
          headers: { 
            'X-Requested-With': 'XMLHttpRequest',
            'x-cors-api-key': 'temp_key'
          }
        })
        if (response.ok) {
          const text = await response.text()
          if (text && text.length > 0) return text
        }
      } else if (proxy.includes('htmldriven')) {
        proxyUrl = proxy + encodeURIComponent(url)
        response = await fetch(proxyUrl)
        if (response.ok) {
          const text = await response.text()
          if (text && text.length > 0) return text
        }
      } else if (proxy.includes('thingproxy')) {
        proxyUrl = proxy + url
        response = await fetch(proxyUrl)
        if (response.ok) {
          const text = await response.text()
          if (text && text.length > 0) return text
        }
      } else if (proxy.includes('codetabs')) {
        proxyUrl = proxy + encodeURIComponent(url)
        response = await fetch(proxyUrl)
        if (response.ok) {
          const text = await response.text()
          if (text && text.length > 0) return text
        }
      } else {
        proxyUrl = proxy + encodeURIComponent(url)
        response = await fetch(proxyUrl)
        if (response.ok) {
          const text = await response.text()
          if (text && text.length > 0) return text
        }
      }
    } catch (error) {
      console.warn(`Proxy ${proxy.substring(0, 30)}... failed:`, error.message)
      continue
    }
  }
  throw new Error('All CORS proxies failed. Please try again later or check if the video has captions.')
}

/**
 * Parse transcript from XML data
 * @param {string} xmlData - XML transcript data
 * @returns {string} Parsed transcript text
 */
function parseTranscriptXML(xmlData) {
  try {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlData, 'text/xml')
    const textElements = xmlDoc.getElementsByTagName('text')
    
    if (!textElements || textElements.length === 0) {
      // Try parsing as HTML if XML parsing fails
      const htmlDoc = parser.parseFromString(xmlData, 'text/html')
      const htmlTextElements = htmlDoc.getElementsByTagName('text') || htmlDoc.querySelectorAll('p')
      
      if (htmlTextElements.length > 0) {
        let transcript = ''
        for (let i = 0; i < htmlTextElements.length; i++) {
          const content = htmlTextElements[i].textContent || htmlTextElements[i].innerText || ''
          transcript += content + ' '
        }
        return cleanTranscriptText(transcript)
      }
    }
    
    let transcript = ''
    for (let i = 0; i < textElements.length; i++) {
      const textContent = textElements[i].textContent || textElements[i].innerText || ''
      transcript += textContent + ' '
    }
    
    return cleanTranscriptText(transcript)
  } catch (error) {
    console.error('Failed to parse transcript XML:', error)
    return ''
  }
}

/**
 * Clean transcript text
 * @param {string} text - Raw transcript text
 * @returns {string} Cleaned text
 */
function cleanTranscriptText(text) {
  if (!text) return ''
  
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Fetch YouTube transcript using multiple methods
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<string>} Transcript text
 */
export async function fetchTranscript(videoId) {
  const methods = [
    () => fetchFromTimedText(videoId),
    () => fetchFromVideoPage(videoId),
    () => fetchFromCaptionAPI(videoId),
  ]
  
  for (const method of methods) {
    try {
      const transcript = await method()
      if (transcript && transcript.length > 50) { // Ensure we got meaningful content
        return transcript
      }
    } catch (error) {
      console.warn('Transcript fetch method failed:', error.message)
    }
  }
  
  throw new Error('No transcript available for this video. The video may not have captions enabled.')
}

/**
 * Method 1: Fetch from YouTube's timedtext API
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<string>} Transcript text
 */
async function fetchFromTimedText(videoId) {
  const formats = ['srv3', 'ttml', 'vtt']
  const languages = ['en', 'en-US', 'en-GB']
  
  for (const lang of languages) {
    for (const fmt of formats) {
      try {
        const url = `https://www.youtube.com/api/timedtext?v=${videoId}&lang=${lang}&fmt=${fmt}`
        const xmlData = await makeProxiedRequest(url)
        
        if (xmlData && xmlData.length > 0) {
          const transcript = parseTranscriptXML(xmlData)
          if (transcript) {
            return transcript
          }
        }
      } catch (error) {
        continue
      }
    }
  }
  
  throw new Error('No captions found via timedtext API')
}

/**
 * Method 2: Extract from video page
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<string>} Transcript text
 */
async function fetchFromVideoPage(videoId) {
  try {
    const videoPageUrl = `https://www.youtube.com/watch?v=${videoId}`
    const html = await makeProxiedRequest(videoPageUrl)
    
    if (!html) {
      throw new Error('Failed to fetch video page')
    }
    
    // Look for player response data
    const playerResponseMatch = html.match(/var ytInitialPlayerResponse = ({.+?});/)
    if (playerResponseMatch) {
      const playerData = JSON.parse(playerResponseMatch[1])
      const captions = playerData?.captions?.playerCaptionsTracklistRenderer?.captionTracks
      
      if (captions && captions.length > 0) {
        // Find English captions
        let captionTrack = captions.find(track => 
          track.languageCode === 'en' || 
          track.languageCode === 'en-US' ||
          track.languageCode === 'en-GB'
        ) || captions[0]
        
        if (captionTrack && captionTrack.baseUrl) {
          const transcriptData = await makeProxiedRequest(captionTrack.baseUrl)
          if (transcriptData) {
            return parseTranscriptXML(transcriptData)
          }
        }
      }
    }
    
    throw new Error('No captions found in video page')
  } catch (error) {
    throw new Error(`Failed to extract from video page: ${error.message}`)
  }
}

/**
 * Method 3: Try direct caption API calls
 * @param {string} videoId - YouTube video ID  
 * @returns {Promise<string>} Transcript text
 */
async function fetchFromCaptionAPI(videoId) {
  try {
    // First get video info to find caption tracks
    const infoUrl = `https://www.youtube.com/get_video_info?video_id=${videoId}`
    const infoData = await makeProxiedRequest(infoUrl)
    
    if (infoData && infoData.includes('caption')) {
      // Parse the info data to find caption URLs
      const decodedData = decodeURIComponent(infoData)
      const captionMatch = decodedData.match(/caption_tracks=([^&]+)/)
      
      if (captionMatch) {
        const captionData = decodeURIComponent(captionMatch[1])
        const urlMatch = captionData.match(/https?:\/\/[^,]+/)
        
        if (urlMatch) {
          const captionUrl = urlMatch[0]
          const transcriptData = await makeProxiedRequest(captionUrl)
          if (transcriptData) {
            return parseTranscriptXML(transcriptData)
          }
        }
      }
    }
    
    throw new Error('No caption API data found')
  } catch (error) {
    throw new Error(`Caption API method failed: ${error.message}`)
  }
}

/**
 * Generate AI summary prompt
 * @param {string} title - Video title
 * @param {string} transcript - Video transcript
 * @returns {string} Formatted prompt
 */
export function createSummaryPrompt(title, transcript) {
  const wordCount = transcript.split(' ').length
  const estimatedDuration = Math.ceil(wordCount / 150)
  
  return `Analyze this YouTube video transcript and provide a comprehensive summary:

**Video Title**: ${title}
**Estimated Duration**: ~${estimatedDuration} minutes
**Word Count**: ${wordCount} words

Please provide your analysis in the following format:

**Key Points**:
• List the main topics and concepts discussed
• Highlight important insights and arguments
• Include specific details and examples mentioned

**Key Takeaways**:
• Provide actionable insights for viewers
• Summarize the most valuable information
• Include practical applications if applicable

**Theme Analysis**:
• Identify the overall theme and purpose
• Note the target audience and context
• Describe the tone and presentation style

**Transcript**:
${transcript}

Please provide a thorough analysis that captures both the content and context of this video.`
}

/**
 * Analyze transcript statistics
 * @param {string} transcript - Video transcript
 * @returns {Object} Statistics
 */
export function analyzeTranscript(transcript) {
  if (!transcript) {
    return {
      wordCount: 0,
      characterCount: 0,
      estimatedDuration: 0,
      readingTime: 0,
      sentences: 0
    }
  }
  
  const words = transcript.split(/\s+/).filter(word => word.length > 0)
  const sentences = transcript.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0)
  
  return {
    wordCount: words.length,
    characterCount: transcript.length,
    estimatedDuration: Math.ceil(words.length / 150), // 150 words per minute speaking
    readingTime: Math.ceil(words.length / 200), // 200 words per minute reading
    sentences: sentences.length
  }
}